<template>
  <div class="axon-page">

    <section class="axon-hero axon-theme-cool">
      <div class="axon-hero-scene">
        <NeuronHero :seed="67" :count="18" :orientation="[1, 0.18, 0.05]" />
      </div>
      <div class="axon-hero-fade" aria-hidden="true" />
      <div class="axon-hero-grid">
        <div class="axon-hero-copy">
          <div class="axon-eyebrow">{{ localize('AXON_HERO_EYEBROW') }}</div>
          <h1 class="axon-display axon-hero-title">
            <span>{{ localize('AXON_HERO_TITLE_LINE_1') }}</span>
            <span class="axon-hero-title-accent">{{ localize('AXON_HERO_TITLE_LINE_2') }}</span>
          </h1>
          <p class="axon-hero-subtitle">{{ localize('AXON_HERO_SUBTITLE') }}</p>
          <div class="axon-hero-actions">
            <button class="axon-button-primary" @click="openWhatsApp">
              <span class="axon-wa-icon" v-html="ICON_WHATSAPP" />
              {{ localize('AXON_HERO_CTA_PRIMARY') }}
            </button>
            <button class="axon-button-ghost" @click="scrollTo('makeen')">
              {{ localize('AXON_HERO_CTA_SECONDARY') }}
            </button>
          </div>
        </div>
      </div>
    </section>

    <section id="makeen" class="axon-makeen">
      <!-- Single graceful curve sweeping down from left to right: the green
           slab's curved top edge rising into the hero. Transparent above the
           curve (hero shows through), solid green below. Control points match
           the dot positions computed for makeenFeatures. -->
      <svg
        class="axon-makeen-curve"
        viewBox="0 0 1440 440"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d="M0,60 C 480,90 920,330 1440,380 L 1440,440 L 0,440 Z"
          fill="#16202B"
        />
      </svg>

      <div class="axon-makeen-glow axon-makeen-glow-b" aria-hidden="true" />
      <div class="axon-makeen-grid-lines" aria-hidden="true" />

      <!-- Feature points hung off the curve -->
      <div class="axon-makeen-points">
        <div
          v-for="f in makeenFeatures"
          :key="f.id"
          class="axon-makeen-point"
          :style="{ '--cx': f.cx, '--dy': f.dy }"
        >
          <span class="axon-makeen-dot" aria-hidden="true" />
          <span class="axon-makeen-line" aria-hidden="true" />
          <article class="axon-makeen-pcard">
            <span class="axon-makeen-pcard-icon" v-html="f.icon" />
            <h3 class="axon-display axon-makeen-pcard-title">{{ localize(f.titleKey) }}</h3>
            <p class="axon-makeen-pcard-body">{{ localize(f.bodyKey) }}</p>
          </article>
        </div>
      </div>

      <div class="axon-container axon-makeen-inner">
        <div class="axon-makeen-head">
          <div class="axon-eyebrow axon-makeen-eyebrow">{{ localize('AXON_MAKEEN_EYEBROW') }}</div>
          <h2 class="axon-display axon-makeen-title">{{ localize('AXON_MAKEEN_TITLE') }}</h2>
          <p class="axon-makeen-lead">{{ localize('AXON_MAKEEN_LEAD') }}</p>
          <p class="axon-makeen-tagline">{{ localize('AXON_MAKEEN_TAGLINE') }}</p>
        </div>

        <div class="axon-makeen-actions">
          <button class="axon-button-primary" @click="openWhatsApp">
            <span class="axon-wa-icon" v-html="ICON_WHATSAPP" />
            {{ localize('AXON_MAKEEN_CTA') }}
          </button>
        </div>
      </div>
    </section>

    <section id="offerings" class="axon-section axon-section-offerings">
      <div class="axon-container axon-offerings">
        <div class="axon-section-head">
          <div class="axon-eyebrow">{{ localize('AXON_OFFER_EYEBROW') }}</div>
          <h2 class="axon-display axon-section-title">{{ localize('AXON_OFFER_TITLE') }}</h2>
          <p class="axon-section-subtitle">{{ localize('AXON_OFFER_SUBTITLE') }}</p>
        </div>
        <div class="axon-offer-grid">
          <article
            v-for="offer in offerings"
            :key="offer.id"
            class="axon-offer-card"
          >
            <span class="axon-offer-icon" v-html="offer.icon" />
            <h3 class="axon-display axon-offer-title">{{ localize(offer.titleKey) }}</h3>
            <p class="axon-offer-body">{{ localize(offer.bodyKey) }}</p>
            <a
              v-if="offer.link"
              :href="offer.link"
              target="_blank"
              rel="noopener"
              class="axon-offer-link"
            >
              {{ localize('AXON_OFFER_LEARN_MORE') }}
            </a>
          </article>
        </div>
      </div>
    </section>

    <section id="services" class="axon-section axon-section-cube">
      <div class="axon-container axon-cube-grid">
        <div class="axon-cube-copy">
          <div class="axon-eyebrow">{{ localize('AXON_CUBE_EYEBROW') }}</div>
          <h2 class="axon-display axon-section-title">{{ localize('AXON_CUBE_TITLE') }}</h2>
          <p class="axon-section-subtitle">{{ localize('AXON_CUBE_SUBTITLE') }}</p>

          <div class="axon-card-grid">
            <article
              v-for="(svc, i) in services"
              :key="svc.key"
              class="axon-card"
              :class="{ 'is-expanded': expandedKey === svc.key, 'is-highlighted': highlightKey === svc.key }"
              :ref="(el) => registerCard(svc.key, el)"
              @click="toggleExpand(svc.key)"
            >
              <div class="axon-card-head">
                <div class="axon-card-num">{{ pad(i + 1) }}</div>
                <h3 class="axon-display axon-card-title">{{ localize(svc.titleKey) }}</h3>
                <span class="axon-card-chev" aria-hidden="true">+</span>
              </div>
              <div class="axon-card-body-wrap" @click.stop>
                <p class="axon-card-body" v-html="localize(svc.bodyKey)" />
                <a
                  v-if="svc.url"
                  :href="svc.url"
                  target="_blank"
                  rel="noopener"
                  class="axon-card-link"
                >
                  {{ localize('AXON_CARD_LEARN_MORE') }} →
                </a>
              </div>
            </article>
          </div>

          <div class="axon-cube-hint">{{ localize('AXON_CUBE_HINT') }}</div>
        </div>
        <div class="axon-cube-stage">
          <AxonHero @select-service="onSelectService" />
        </div>
      </div>
    </section>

    <section class="axon-section axon-section-approach">
      <div class="axon-container axon-approach-grid">
        <div>
          <div class="axon-eyebrow">{{ localize('AXON_APPROACH_EYEBROW') }}</div>
          <h2 class="axon-display axon-section-title">{{ localize('AXON_APPROACH_TITLE') }}</h2>
        </div>
        <div class="axon-approach-body">
          <p>{{ localize('AXON_APPROACH_BODY') }}</p>
        </div>
      </div>
    </section>

    <section id="contact" class="axon-section axon-section-contact">
      <div class="axon-container">
        <div class="axon-contact-card">
          <svg
            class="axon-contact-mesh"
            viewBox="0 0 400 300"
            preserveAspectRatio="xMaxYMid slice"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="hexMesh" x1="0" y1="0" x2="400" y2="300" gradientUnits="userSpaceOnUse">
                <stop offset="0" stop-color="#00A651" stop-opacity="0.85" />
                <stop offset="1" stop-color="#005C2A" stop-opacity="0.4" />
              </linearGradient>
            </defs>
            <g fill="none" stroke="url(#hexMesh)" stroke-width="1.2">
              <polygon points="286,60 303,30.6 337,30.6 354,60 337,89.4 303,89.4" />
              <polygon points="286,119 303,89.6 337,89.6 354,119 337,148.4 303,148.4" />
              <polygon points="286,178 303,148.6 337,148.6 354,178 337,207.4 303,207.4" />
              <polygon points="286,237 303,207.6 337,207.6 354,237 337,266.4 303,266.4" />
              <polygon points="337,89 354,59.6 388,59.6 405,89 388,118.4 354,118.4" />
              <polygon points="337,148 354,118.6 388,118.6 405,148 388,177.4 354,177.4" />
              <polygon points="337,207 354,177.6 388,177.6 405,207 388,236.4 354,236.4" />
            </g>
            <g fill="#2FBF6F" fill-opacity="0.9">
              <circle cx="337" cy="89.6" r="2.4" />
              <circle cx="354" cy="119" r="2.4" />
              <circle cx="337" cy="148.4" r="2.4" />
              <circle cx="388" cy="118.4" r="2.4" />
            </g>
          </svg>
          <div class="axon-eyebrow axon-eyebrow-light">{{ localize('AXON_CONTACT_EYEBROW') }}</div>
          <h2 class="axon-display axon-contact-title">{{ localize('AXON_CONTACT_TITLE') }}</h2>
          <button class="axon-button-on-green" @click="openContact">
            {{ localize('AXON_CONTACT_CTA') }}
            <span class="axon-arrow">→</span>
          </button>
        </div>
      </div>
    </section>

    <!-- Contact message box -->
    <transition name="axon-modal-fade">
      <div
        v-if="showContact"
        class="axon-modal-overlay"
        @click.self="closeContact"
      >
        <div class="axon-modal" role="dialog" aria-modal="true">
          <button class="axon-modal-close" aria-label="Close" @click="closeContact">×</button>

          <div v-if="contactStatus !== 'sent'" class="axon-modal-body">
            <div class="axon-eyebrow">{{ localize('AXON_CONTACT_EYEBROW') }}</div>
            <h3 class="axon-display axon-modal-title">{{ localize('AXON_CONTACT_FORM_TITLE') }}</h3>
            <p class="axon-modal-sub">{{ localize('AXON_CONTACT_FORM_SUB') }}</p>

            <form class="axon-form" @submit.prevent="submitContact">
              <label class="axon-field">
                <span class="axon-field-label">{{ localize('AXON_CONTACT_FORM_NAME') }}</span>
                <input
                  v-model="contactForm.name"
                  type="text"
                  class="axon-input"
                  :placeholder="localize('AXON_CONTACT_FORM_NAME_PH')"
                  autocomplete="name"
                />
              </label>
              <label class="axon-field">
                <span class="axon-field-label">{{ localize('AXON_CONTACT_FORM_EMAIL') }}</span>
                <input
                  v-model="contactForm.email"
                  type="email"
                  class="axon-input"
                  :placeholder="localize('AXON_CONTACT_FORM_EMAIL_PH')"
                  autocomplete="email"
                />
              </label>
              <label class="axon-field">
                <span class="axon-field-label">{{ localize('AXON_CONTACT_FORM_MESSAGE') }}</span>
                <textarea
                  v-model="contactForm.message"
                  class="axon-input axon-textarea"
                  rows="5"
                  :placeholder="localize('AXON_CONTACT_FORM_MESSAGE_PH')"
                />
              </label>

              <p v-if="contactStatus === 'error'" class="axon-form-error">{{ contactError }}</p>

              <button
                type="submit"
                class="axon-button-on-green axon-form-submit"
                :disabled="contactStatus === 'sending'"
              >
                {{ contactStatus === 'sending'
                  ? localize('AXON_CONTACT_FORM_SENDING')
                  : localize('AXON_CONTACT_FORM_SEND') }}
                <span class="axon-arrow">→</span>
              </button>
            </form>
          </div>

          <div v-else class="axon-modal-body axon-modal-success">
            <div class="axon-success-mark" aria-hidden="true">✓</div>
            <h3 class="axon-display axon-modal-title">{{ localize('AXON_CONTACT_FORM_SUCCESS_TITLE') }}</h3>
            <p class="axon-modal-sub">{{ localize('AXON_CONTACT_FORM_SUCCESS_SUB') }}</p>
            <button class="axon-button-on-green axon-form-submit" @click="closeContact">
              {{ localize('AXON_CONTACT_FORM_DONE') }}
            </button>
          </div>
        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import { ref, reactive } from "vue";
import Axios from "axios";
import AxonHero from "./AxonHero.vue";
import NeuronHero from "./NeuronHero.vue";
import { translationStore } from "@/stores/translation";

// Trusted, static stroke line-icons (NOT user input) — safe for v-html.
const ICON_SHIELD =
  '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l7 3v5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6l7-3z"/><path d="M9 12l2 2 4-4"/></svg>';
const ICON_RADAR =
  '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3l7 3v5c0 4.4-3 7.6-7 9-4-1.4-7-4.6-7-9V6l7-3z"/><circle cx="12" cy="11" r="1.5"/><path d="M12 11l3-2.2"/><path d="M9.2 13.8a4 4 0 0 1 5.6-5.6"/></svg>';
const ICON_AGENT =
  '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="5" y="7" width="14" height="11" rx="2"/><path d="M12 4v3"/><circle cx="9.5" cy="12" r="1"/><circle cx="14.5" cy="12" r="1"/><path d="M9.5 15.5h5"/><path d="M2 11v3M22 11v3"/></svg>';
const ICON_PULSE =
  '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20.8 11c.13-.55.2-1.13.2-1.7A4.3 4.3 0 0 0 12 6.5 4.3 4.3 0 0 0 3 9.3c0 4.2 5.4 8 9 10"/><path d="M3 13h3l2-3 3 6 2-3h6"/></svg>';

// Makeen feature icons (trusted, static stroke line-icons).
const ICON_REDACT =
  '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4 6h16"/><rect x="4" y="10.5" width="11" height="3.2" rx="1" fill="currentColor" stroke="none"/><path d="M4 18h16"/></svg>';
const ICON_INTENT =
  '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3.2"/><path d="M12 2v3M12 19v3M2 12h3M19 12h3M5 5l2.1 2.1M16.9 16.9 19 19M19 5l-2.1 2.1M7.1 16.9 5 19"/></svg>';
const ICON_AUDIT =
  '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z"/><path d="M14 3v5h5"/><path d="M9 13l2 2 4-4"/></svg>';
const ICON_DEPLOY =
  '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="12" rx="2"/><path d="M8 20h8M12 16v4"/><path d="M7.5 9.5l2 2 4-4"/></svg>';

// WhatsApp glyph — official Font Awesome brand path (solid, inherits currentColor).
const ICON_WHATSAPP =
  '<svg viewBox="0 0 448 512" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M380.9 97.1C339 55.1 283.2 32 223.9 32c-122.4 0-222 99.6-222 222 0 39.1 10.2 77.3 29.6 111L0 480l117.7-30.9c32.4 17.7 68.9 27 106.1 27h.1c122.3 0 224.1-99.6 224.1-222 0-59.3-25.2-115-67.2-157zM223.9 438.6c-33.2 0-65.7-8.9-94-25.7l-6.7-4-69.8 18.3L72 359.2l-4.4-7c-18.5-29.4-28.2-63.3-28.2-98.2 0-101.7 82.8-184.5 184.6-184.5 49.3 0 95.6 19.2 130.4 54.1 34.8 34.9 56.2 81.2 56.1 130.5 0 101.8-84.9 184.5-186.6 184.5zm101.2-138.2c-5.5-2.8-32.8-16.2-37.9-18-5.1-1.9-8.8-2.8-12.5 2.8-3.7 5.6-14.3 18-17.6 21.8-3.2 3.7-6.5 4.2-12 1.4-32.6-16.3-54-29.1-75.5-66-5.7-9.8 5.7-9.1 16.3-30.3 1.8-3.7.9-6.9-.5-9.7-1.4-2.8-12.5-30.1-17.1-41.2-4.5-10.8-9.1-9.3-12.5-9.5-3.2-.2-6.9-.2-10.6-.2-3.7 0-9.7 1.4-14.8 6.9-5.1 5.6-19.4 19-19.4 46.3 0 27.3 19.9 53.7 22.6 57.4 2.8 3.7 39.1 59.7 94.8 83.8 35.2 15.2 49 16.5 66.6 13.9 10.7-1.6 32.8-13.4 37.4-26.4 4.6-13 4.6-24.1 3.2-26.4-1.3-2.5-5-3.9-10.5-6.6z"/></svg>';

export default {
  name: "Home",
  components: { AxonHero, NeuronHero },
  setup() {
    const translation = translationStore();

    const offerings = [
      {
        id: "data-protection",
        icon: ICON_SHIELD,
        titleKey: "AXON_OFFER_1_TITLE",
        bodyKey: "AXON_OFFER_1_BODY",
        link: null,
      },
      {
        id: "enbarr",
        icon: ICON_RADAR,
        titleKey: "AXON_OFFER_2_TITLE",
        bodyKey: "AXON_OFFER_2_BODY",
        link: "https://enbarr.ai",
      },
      {
        id: "kaiju",
        icon: ICON_AGENT,
        titleKey: "AXON_OFFER_3_TITLE",
        bodyKey: "AXON_OFFER_3_BODY",
        link: null,
      },
      {
        id: "clinical-intelligence",
        icon: ICON_PULSE,
        titleKey: "AXON_OFFER_4_TITLE",
        bodyKey: "AXON_OFFER_4_BODY",
        link: null,
      },
    ];
    // Dots sit ON the curve (full-bleed, viewBox 1440x440). cx/dy are sampled
    // directly from the aggressive cubic bezier P0(0,50) P1(500,70) P2(900,380)
    // P3(1440,410) at t = 0.18 / 0.40 / 0.63 / 0.85, so each box follows the
    // curve's steep descent.
    const makeenFeatures = [
      { id: "redact", icon: ICON_REDACT, titleKey: "AXON_MAKEEN_F1_TITLE", bodyKey: "AXON_MAKEEN_F1_BODY", cx: "18%", dy: "94px" },
      { id: "intent", icon: ICON_INTENT, titleKey: "AXON_MAKEEN_F2_TITLE", bodyKey: "AXON_MAKEEN_F2_BODY", cx: "39%", dy: "171px" },
      { id: "audit",  icon: ICON_AUDIT,  titleKey: "AXON_MAKEEN_F3_TITLE", bodyKey: "AXON_MAKEEN_F3_BODY", cx: "62%", dy: "267px" },
      { id: "deploy", icon: ICON_DEPLOY, titleKey: "AXON_MAKEEN_F4_TITLE", bodyKey: "AXON_MAKEEN_F4_BODY", cx: "84%", dy: "346px" },
    ];

    const cardRefs = reactive({});
    const highlightKey = ref(null);
    let highlightTimer = null;

    const services = [
      { key: "consulting",  titleKey: "AXON_SVC_CONSULTING_TITLE",  bodyKey: "AXON_SVC_CONSULTING_BODY" },
      { key: "sovereignty", titleKey: "AXON_SVC_SOVEREIGNTY_TITLE", bodyKey: "AXON_SVC_SOVEREIGNTY_BODY" },
      { key: "cybersec",    titleKey: "AXON_SVC_CYBERSEC_TITLE",    bodyKey: "AXON_SVC_CYBERSEC_BODY", url: "https://enbarr.ai" },
      { key: "maintenance", titleKey: "AXON_SVC_MAINTENANCE_TITLE", bodyKey: "AXON_SVC_MAINTENANCE_BODY" },
      { key: "cloud",       titleKey: "AXON_SVC_CLOUD_TITLE",       bodyKey: "AXON_SVC_CLOUD_BODY" },
      { key: "agentic",     titleKey: "AXON_SVC_AGENTIC_TITLE",     bodyKey: "AXON_SVC_AGENTIC_BODY" },
      { key: "software",    titleKey: "AXON_SVC_SOFTWARE_TITLE",    bodyKey: "AXON_SVC_SOFTWARE_BODY" },
      { key: "model",       titleKey: "AXON_SVC_MODEL_TITLE",       bodyKey: "AXON_SVC_MODEL_BODY" },
    ];

    const expandedKey = ref(null);
    const toggleExpand = (key) => {
      expandedKey.value = expandedKey.value === key ? null : key;
    };

    const registerCard = (key, el) => {
      if (el) cardRefs[key] = el;
    };

    const scrollTo = (id) => {
      const target = document.getElementById(id);
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const onSelectService = (key) => {
      const el = cardRefs[key];
      if (!el) return;
      expandedKey.value = key;
      highlightKey.value = key;
      // Scroll only on small screens — on desktop the cube and cards are
      // side-by-side in the same section, so the card is already on screen
      // and a forced scroll is jarring. On mobile the cube stacks above the
      // cards, so scrolling to the matching card is genuinely helpful.
      if (
        typeof window !== "undefined" &&
        window.matchMedia("(max-width: 980px)").matches
      ) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      clearTimeout(highlightTimer);
      highlightTimer = setTimeout(() => (highlightKey.value = null), 1600);
    };

    // --- Contact message box ---
    const showContact = ref(false);
    const contactForm = reactive({ name: "", email: "", message: "" });
    const contactStatus = ref("idle"); // idle | sending | sent | error
    const contactError = ref("");

    const openContact = () => {
      contactStatus.value = "idle";
      contactError.value = "";
      showContact.value = true;
    };

    // WhatsApp "get in touch" — Saudi number 055 385 2406 → +966 55 385 2406.
    const openWhatsApp = () => {
      window.open("https://wa.me/966553852406", "_blank", "noopener");
    };

    const closeContact = () => {
      showContact.value = false;
    };

    const submitContact = async () => {
      const name = contactForm.name.trim();
      const email = contactForm.email.trim();
      const message = contactForm.message.trim();

      if (!name || !email || !message) {
        contactStatus.value = "error";
        contactError.value = translation.localize("AXON_CONTACT_FORM_REQUIRED");
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        contactStatus.value = "error";
        contactError.value = translation.localize("AXON_CONTACT_FORM_BADEMAIL");
        return;
      }

      contactStatus.value = "sending";
      contactError.value = "";
      try {
        // Relative, same-origin URL — inherits the page's protocol/host so an
        // HTTPS page never falls back to the http:// VUE_APP_SERVER_URI value
        // (which the browser blocks as mixed content).
        await Axios.post("/contact", {
          name,
          email,
          message,
        });
        contactStatus.value = "sent";
        contactForm.name = "";
        contactForm.email = "";
        contactForm.message = "";
      } catch (e) {
        contactStatus.value = "error";
        contactError.value = translation.localize("AXON_CONTACT_FORM_FAILED");
      }
    };

    const pad = (n) => String(n).padStart(2, "0");

    return {
      services,
      offerings,
      makeenFeatures,
      cardRefs,
      registerCard,
      scrollTo,
      onSelectService,
      openContact,
      openWhatsApp,
      ICON_WHATSAPP,
      closeContact,
      submitContact,
      showContact,
      contactForm,
      contactStatus,
      contactError,
      pad,
      highlightKey,
      expandedKey,
      toggleExpand,
      localize: translation.localize,
    };
  },
};
</script>

<style scoped>
.axon-page {
  width: 100vw;
  margin-left: calc(50% - 50vw);
  margin-right: 0;
  max-width: none;
  padding: 0;
  background: var(--axon-white);
  color: var(--axon-ink-900);
  font-family: var(--axon-font-body);
  /* No overflow clipping — `overflow-x: hidden` here was implicitly clipping
     overflow-y too, and that was eating the canvas's negative margin-top
     under the header. */
}

/* === Hero === */
.axon-hero {
  position: relative;
  /* margin-top pulls the hero up under the fixed 100px header on desktop;
     padding-top adds the header clearance back inside so copy doesn't sit
     under the navbar. Net: canvas extends to the very top of the viewport. */
  margin-top: -100px;
  /* Extra bottom padding leaves room for the neuron canvas to breathe lower
     down before the sharp diagonal cut into the bright cards below. */
  padding: 160px 24px 320px;
  min-height: 104vh;
  overflow: hidden;
  isolation: isolate;
  /* Cool tinted panel so the neuron canvas reads as its own zone, distinct
     from the bright white offerings section that slices up into it. */
  background: linear-gradient(180deg, #ECF4FB 0%, #DFEDF8 55%, #D3E5F3 100%);
}

/* Cool theme — scoped to the top hero only. Bluish-grey scheme for the
   eyebrow, headline accent, and subtitle. The page below stays brand-green. */
.axon-theme-cool .axon-eyebrow {
  color: #3F6B8C;
}
.axon-theme-cool .axon-hero-title {
  color: #1F2A36;
}
.axon-theme-cool .axon-hero-title-accent {
  background: linear-gradient(120deg, #3578C4 0%, #6EB8E0 50%, #4D9ADD 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  /* `background-clip: text` paints the gradient inside the element box,
     so descenders (g, p, y, …) only appear if line-height + padding leave
     room for them. Tight 1.05 line-height was clipping the "g" in
     "Kingdom"; relax to 1.2 + tiny padding-bottom for safety. */
  line-height: 1.2;
  padding-bottom: 0.05em;
  filter:
    drop-shadow(0 0 20px rgba(125, 211, 252, 0.45))
    drop-shadow(0 0 32px rgba(255, 178, 212, 0.20));
}
.axon-theme-cool .axon-hero-subtitle {
  color: #3F4F5E;
}
.axon-theme-cool .axon-hero-fade {
  /* No scrim — the neurons read at full strength. */
  background: none;
}
.axon-theme-cool .axon-button-ghost {
  border-color: #C7D6E4;
  color: #1F2A36;
}
.axon-theme-cool .axon-button-ghost:hover {
  border-color: #5C8CB0;
  background: #EAF2F8;
}
.axon-hero-scene {
  position: absolute;
  inset: 0;
  z-index: 1;
}
.axon-hero-fade {
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
  background: none;
}

.axon-hero-grid {
  position: relative;
  z-index: 3;
  max-width: 1280px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  min-height: calc(100vh - 180px);
  pointer-events: none;
}
.axon-hero-copy {
  display: flex;
  flex-direction: column;
  gap: 28px;
  max-width: 540px;
  pointer-events: auto;
  position: relative;
}
.axon-hero-title {
  font-size: clamp(2.4rem, 5.8vw, 4.6rem);
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.05em;
}
.axon-hero-title-accent {
  background: linear-gradient(120deg, var(--axon-green-700), var(--axon-green-400));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.axon-hero-subtitle {
  font-size: clamp(1.05rem, 1.4vw, 1.2rem);
  line-height: 1.55;
  color: var(--axon-ink-700);
  margin: 0;
  max-width: 520px;
}
.axon-hero-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 4px;
}
.axon-wa-icon {
  display: inline-flex;
  align-items: center;
  margin-right: 2px;
}
.axon-wa-icon :deep(svg) {
  display: block;
}
.axon-arrow {
  display: inline-block;
  transition: transform 220ms ease;
}
.axon-button-primary:hover .axon-arrow {
  transform: translateX(3px);
}
.axon-hero-hint {
  font-size: 0.85rem;
  color: var(--axon-ink-500);
  border-left: 2px solid var(--axon-green-400);
  padding-left: 12px;
  margin-top: 8px;
  max-width: 460px;
  line-height: 1.5;
}

/* === Sections === */
.axon-section {
  padding: 120px 24px;
  position: relative;
}
.axon-container {
  max-width: 1280px;
  margin: 0 auto;
}
.axon-section-head {
  max-width: 760px;
  margin-bottom: 64px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.axon-section-title {
  font-size: clamp(2rem, 4vw, 3.2rem);
  margin: 0;
  color: var(--axon-ink-900);
}
.axon-section-subtitle {
  font-size: clamp(1rem, 1.2vw, 1.1rem);
  line-height: 1.6;
  color: var(--axon-ink-700);
  max-width: 640px;
  margin: 0;
}

/* === Services grid === */
.axon-services-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 22px;
}
.axon-service-card {
  position: relative;
  background: var(--axon-white);
  border: 1px solid var(--axon-ink-100);
  border-radius: var(--axon-radius-lg);
  padding: 36px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  overflow: hidden;
  transition: transform 320ms cubic-bezier(0.2, 0.8, 0.2, 1),
              border-color 320ms ease,
              box-shadow 320ms ease,
              background 320ms ease;
}
.axon-service-card:hover,
.axon-service-card.is-highlighted {
  transform: translateY(-4px);
  border-color: var(--axon-green-300);
  box-shadow: var(--axon-shadow-lg);
  background: linear-gradient(180deg, #ffffff 0%, var(--axon-green-050) 100%);
}
.axon-service-card.is-highlighted {
  animation: axon-pulse 1.4s ease-out;
}
@keyframes axon-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(0, 166, 81, 0.45); }
  100% { box-shadow: 0 0 0 24px rgba(0, 166, 81, 0); }
}
.axon-service-card-bar {
  position: absolute;
  top: 0;
  left: 32px;
  width: 36px;
  height: 3px;
  background: var(--axon-green-600);
  border-radius: 0 0 4px 4px;
  transition: width 320ms ease;
}
.axon-service-card:hover .axon-service-card-bar,
.axon-service-card.is-highlighted .axon-service-card-bar {
  width: 64px;
}
.axon-service-card-num {
  font-family: var(--axon-font-body);
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  color: var(--axon-green-600);
}
.axon-service-card-title {
  font-size: 1.45rem;
  margin: 0;
  color: var(--axon-ink-900);
  line-height: 1.15;
}
.axon-service-card-body {
  font-size: 0.98rem;
  line-height: 1.55;
  color: var(--axon-ink-700);
  margin: 0;
}

/* === Combined services + cube section === */
.axon-section-cube {
  background: linear-gradient(180deg, #ffffff 0%, var(--axon-green-050) 100%);
  border-top: 1px solid var(--axon-ink-100);
  border-bottom: 1px solid var(--axon-ink-100);
  padding: 100px 24px;
}
.axon-cube-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
  gap: 56px;
  align-items: center;
}
.axon-cube-copy {
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-width: 680px;
}
.axon-cube-stage {
  position: relative;
  height: clamp(500px, 72vh, 720px);
  width: 100%;
}
.axon-cube-hint {
  font-size: 0.82rem;
  color: var(--axon-ink-500);
  border-left: 2px solid var(--axon-green-400);
  padding-left: 12px;
  margin-top: 4px;
  max-width: 480px;
  line-height: 1.5;
}

/* --- card grid (8 minimized boxes, click to expand) --- */
.axon-card-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 14px;
}
.axon-card {
  background: white;
  border: 1px solid var(--axon-ink-100);
  border-radius: 14px;
  padding: 14px 16px;
  cursor: pointer;
  transition:
    border-color 240ms ease,
    box-shadow 240ms ease,
    transform 240ms ease,
    background 240ms ease;
  position: relative;
}
.axon-card:hover {
  border-color: var(--axon-green-300);
  transform: translateY(-1px);
}
.axon-card.is-expanded {
  border-color: var(--axon-green-600);
  background: linear-gradient(180deg, #ffffff 0%, var(--axon-green-050) 100%);
  box-shadow: var(--axon-shadow-md);
  grid-column: 1 / -1; /* expanded card spans the full row */
}
.axon-card.is-highlighted {
  animation: axon-pulse 1.4s ease-out;
}
.axon-card-head {
  display: flex;
  align-items: center;
  gap: 10px;
}
.axon-card-num {
  font-family: var(--axon-font-body);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  color: var(--axon-green-700);
  background: var(--axon-green-050);
  padding: 3px 8px;
  border-radius: 999px;
  flex: 0 0 auto;
  border: 1px solid var(--axon-green-100);
}
.axon-card.is-expanded .axon-card-num {
  background: var(--axon-green-600);
  color: var(--axon-white);
  border-color: var(--axon-green-600);
}
.axon-card-title {
  font-size: 0.95rem;
  margin: 0;
  font-weight: 600;
  color: var(--axon-ink-900);
  flex: 1;
  line-height: 1.2;
}
.axon-card-chev {
  font-family: var(--axon-font-body);
  font-size: 1.1rem;
  font-weight: 300;
  color: var(--axon-ink-300);
  transition: transform 280ms cubic-bezier(0.2, 0.8, 0.2, 1), color 240ms ease;
  flex: 0 0 auto;
  line-height: 1;
}
.axon-card.is-expanded .axon-card-chev {
  transform: rotate(45deg);
  color: var(--axon-green-600);
}
.axon-card-body-wrap {
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transition: max-height 320ms ease, opacity 220ms ease, margin-top 220ms ease;
}
.axon-card.is-expanded .axon-card-body-wrap {
  max-height: 240px;
  opacity: 1;
  margin-top: 12px;
}
.axon-card-body {
  font-size: 0.9rem;
  line-height: 1.55;
  color: var(--axon-ink-700);
  margin: 0;
}
.axon-card-link {
  display: inline-block;
  margin-top: 10px;
  color: var(--axon-green-700);
  font-weight: 600;
  text-decoration: none;
  font-size: 0.85rem;
  transition: color 200ms ease;
}
.axon-card-link:hover {
  color: var(--axon-green-600);
  text-decoration: underline;
}

/* Inline link styling for v-html'd anchor tags inside card bodies (e.g. the
   "Enbarr.ai" mention in the cybersecurity card). Needs :deep() to pierce
   scoped-style boundaries since the anchor is injected via v-html. */
.axon-card-body :deep(.axon-inline-link) {
  color: var(--axon-green-700);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 200ms ease;
}
.axon-card-body :deep(.axon-inline-link:hover) {
  color: var(--axon-green-600);
}

/* === Makeen — full-screen feature section === */
.axon-makeen {
  position: relative;
  display: flex;
  align-items: flex-start;
  /* Pull up so the curved green top overlaps the bottom of the hero canvas —
     the top region of this section is transparent, so the hero's 3D shows
     through and appears to flow straight into the green below. */
  margin-top: -380px;
  /* padding-top clears the curve (440) + the cards hanging off its descent,
     then the heading follows with generous room. Taller overall. */
  padding: 700px 24px 220px;
  overflow: hidden;
  isolation: isolate;
  color: var(--axon-white);
  /* Transparent down to the curve's solid line (380px), then full slate-navy.
     Above that line the hero canvas shows through. */
  background:
    radial-gradient(120% 90% at 0% 100%, rgba(8, 13, 20, 0.55), transparent 60%),
    linear-gradient(
      to bottom,
      transparent 0,
      transparent 380px,
      #16202B 380px,
      #1B2733 64%,
      #223041 100%
    );
}
/* The big sweeping curve — now GREEN, filling BELOW the curve line with a
   transparent top, so it reads as the curved top edge of the green slab
   rising into the hero. Same green as the slab → seamless. */
.axon-makeen-curve {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 440px;
  z-index: 0;
  pointer-events: none;
  display: block;
  /* Soft shadow above the curve edge for a little depth against the hero. */
  filter: drop-shadow(0 -4px 12px rgba(8, 13, 20, 0.2));
}
.axon-makeen-grid-lines {
  position: absolute;
  inset: -1px;
  z-index: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(150, 190, 235, 0.10) 1px, transparent 1px),
    linear-gradient(90deg, rgba(150, 190, 235, 0.10) 1px, transparent 1px);
  background-size: 64px 64px;
  -webkit-mask-image: radial-gradient(120% 80% at 70% 55%, #000 0%, transparent 78%);
  mask-image: radial-gradient(120% 80% at 70% 55%, #000 0%, transparent 78%);
}
.axon-makeen-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  z-index: 0;
  pointer-events: none;
}
.axon-makeen-glow-b {
  bottom: -140px;
  left: -100px;
  width: 460px;
  height: 460px;
  background: radial-gradient(circle, rgba(0, 166, 81, 0.22), transparent 70%);
}

/* --- Feature points hung off the curve --- */
/* Full-bleed (no max-width) so cx% maps to the same stretched space as the
   curve (preserveAspectRatio="none"), keeping every dot on the line. */
.axon-makeen-points {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 440px;
  z-index: 2;
  pointer-events: none;
}
.axon-makeen-point {
  position: absolute;
  top: 0;
  left: var(--cx);
  /* Anchor each point on the descending curve via its individual drop. */
  transform: translateX(-50%) translateY(var(--dy));
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 210px;
  pointer-events: auto;
}
/* The dot sitting on the curve. */
.axon-makeen-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #2FBF6F;
  border: 3px solid #ffffff;
  box-shadow: 0 0 0 4px rgba(0, 108, 53, 0.18), 0 2px 6px rgba(0, 45, 22, 0.35);
  flex: 0 0 auto;
  z-index: 1;
}
/* Connector dropping from the dot down to the card. */
.axon-makeen-line {
  width: 2px;
  height: 30px;
  background: linear-gradient(to bottom, #2FBF6F, rgba(47, 191, 111, 0.15));
  flex: 0 0 auto;
}
/* The compact card hanging below. */
.axon-makeen-pcard {
  width: 100%;
  padding: 20px 18px;
  border-radius: var(--axon-radius-lg);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(150, 190, 235, 0.18);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: transform 320ms cubic-bezier(0.2, 0.8, 0.2, 1),
              border-color 320ms ease,
              background 320ms ease,
              box-shadow 320ms ease;
}
.axon-makeen-point:hover .axon-makeen-pcard {
  transform: translateY(-4px);
  border-color: rgba(47, 191, 111, 0.5);
  background: rgba(255, 255, 255, 0.1);
  box-shadow: 0 24px 48px -20px rgba(0, 0, 0, 0.6);
}
.axon-makeen-pcard-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 14px;
  border-radius: var(--axon-radius-md);
  background: rgba(47, 191, 111, 0.18);
  border: 1px solid rgba(47, 191, 111, 0.35);
  color: #6FE0A4;
}
.axon-makeen-pcard-title {
  font-size: 1.02rem;
  margin: 0 0 8px;
  color: var(--axon-white);
  line-height: 1.2;
}
.axon-makeen-pcard-body {
  font-size: 0.86rem;
  line-height: 1.5;
  color: rgba(224, 233, 245, 0.74);
  margin: 0;
}

.axon-makeen-inner {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 64px;
  width: 100%;
}
.axon-makeen-head {
  max-width: 820px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.axon-makeen-eyebrow {
  color: #6FE0A4;
}
.axon-makeen-title {
  font-size: clamp(2.2rem, 4.6vw, 3.6rem);
  margin: 0;
  color: var(--axon-white);
  line-height: 1.04;
}
.axon-makeen-lead {
  font-size: clamp(1.05rem, 1.5vw, 1.25rem);
  line-height: 1.6;
  color: rgba(224, 233, 245, 0.85);
  margin: 0;
  max-width: 760px;
}
.axon-makeen-tagline {
  font-family: var(--axon-font-display);
  font-weight: 700;
  font-size: clamp(1.2rem, 2vw, 1.6rem);
  margin: 4px 0 0;
  /* Bright green gradient text. */
  background: linear-gradient(100deg, #9CF0C2 0%, #2FBF6F 50%, #00A651 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.axon-makeen-actions {
  display: flex;
}

/* === Offerings (intermediate section) === */
.axon-section-offerings {
  position: relative;
  /* Pull the bright panel up so it slices into the cool Makeen slab, then
     restore inner spacing with padding. */
  margin-top: -140px;
  padding-top: 200px;
  background: var(--axon-white);
  border-bottom: 1px solid var(--axon-ink-100);
  /* Sharp diagonal top edge — the crisp cut from the cool canvas zone into
     the bright cards. Right side rides higher than the left for the angle. */
  clip-path: polygon(0 140px, 100% 0, 100% 100%, 0 100%);
}
/* A single crisp hairline riding the diagonal cut — a clean seam between the
   cool canvas and the bright panel. Thin gradient stroke, fading at both ends
   so it reads as light catching an edge rather than a drawn bar. Polygon is
   0/100% anchored so it tracks the cut at any width. */
.axon-section-offerings::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
  background: linear-gradient(
    90deg,
    rgba(75, 163, 232, 0) 0%,
    rgba(75, 163, 232, 0.55) 30%,
    rgba(46, 137, 214, 0.7) 60%,
    rgba(46, 137, 214, 0) 100%
  );
  clip-path: polygon(0 140px, 100% 0, 100% 1.5px, 0 141.5px);
}
.axon-offerings {
  position: relative;
  z-index: 3;
}
.axon-offer-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 22px;
}
.axon-offer-card {
  position: relative;
  display: flex;
  flex-direction: column;
  background: var(--axon-white);
  border: 1px solid var(--axon-ink-100);
  border-radius: var(--axon-radius-lg);
  padding: 30px 28px;
  overflow: hidden;
  transition: transform 320ms cubic-bezier(0.2, 0.8, 0.2, 1),
              border-color 320ms ease,
              box-shadow 320ms ease;
}
.axon-offer-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  /* Sharp royal-green tactical edge that wipes in on hover. */
  background: var(--axon-royal-500);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 320ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
.axon-offer-card:hover {
  transform: translateY(-4px);
  border-color: var(--axon-blue-300);
  box-shadow: var(--axon-shadow-lg);
}
.axon-offer-card:hover::before {
  transform: scaleX(1);
}
.axon-offer-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  margin-bottom: 20px;
  border-radius: var(--axon-radius-md);
  background: var(--axon-green-050);
  color: var(--axon-green-600);
  border: 1px solid var(--axon-green-100);
  transition: background 320ms ease, color 320ms ease, border-color 320ms ease;
}
.axon-offer-card:hover .axon-offer-icon {
  background: var(--axon-green-600);
  color: var(--axon-white);
  border-color: var(--axon-green-600);
}
.axon-offer-title {
  font-size: 1.2rem;
  margin: 0 0 12px;
  color: var(--axon-ink-900);
  line-height: 1.2;
}
.axon-offer-body {
  font-size: 0.95rem;
  line-height: 1.55;
  color: var(--axon-ink-700);
  margin: 0;
}
.axon-offer-link {
  display: inline-block;
  margin-top: 16px;
  color: var(--axon-green-700);
  font-family: var(--axon-font-body);
  font-weight: 600;
  font-size: 0.9rem;
  text-decoration: none;
  transition: color 200ms ease;
}
.axon-offer-link:hover {
  color: var(--axon-green-600);
  text-decoration: underline;
}

/* === Approach === */
.axon-section-approach {
  position: relative;
  /* Cool icy panel with a deep navy floor — gives the lower segment a
     gradient that bleeds toward dark, per the cool brief. */
  background: linear-gradient(170deg, var(--axon-blue-050) 0%, #E3EFFA 60%, #D2E5F5 100%);
  border-bottom: 1px solid var(--axon-ink-100);
  overflow: hidden;
}
/* Twin angular accent lines slicing across the top of the lower segment —
   parallel, one icy blue and one royal green, echoing the canvas transition.
   They ride a shallow diagonal and glide in subtly. */
.axon-section-approach::before,
.axon-section-approach::after {
  content: "";
  position: absolute;
  left: -10%;
  width: 120%;
  height: 2px;
  transform: rotate(-2.4deg);
  transform-origin: left center;
  pointer-events: none;
}
.axon-section-approach::before {
  top: 46px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(75, 163, 232, 0) 8%,
    var(--axon-blue-400) 42%,
    var(--axon-blue-500) 60%,
    rgba(46, 137, 214, 0) 92%
  );
  box-shadow: 0 0 18px rgba(75, 163, 232, 0.45);
  opacity: 0.9;
}
.axon-section-approach::after {
  /* Parallel royal-green hairline, offset just below — the sharp tactical twin. */
  top: 54px;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(0, 166, 81, 0) 18%,
    var(--axon-royal-500) 50%,
    rgba(0, 166, 81, 0) 80%
  );
  box-shadow: 0 0 10px rgba(0, 166, 81, 0.4);
  opacity: 0.75;
}
.axon-approach-grid {
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.2fr);
  gap: 80px;
  align-items: start;
}
.axon-approach-body p {
  font-size: clamp(1.1rem, 1.4vw, 1.35rem);
  line-height: 1.55;
  color: var(--axon-ink-700);
  margin: 0;
  font-weight: 400;
}

/* === Contact === */
.axon-section-contact {
  padding: 60px 24px 120px;
}
.axon-contact-card {
  background:
    radial-gradient(at 80% 20%, rgba(75, 163, 232, 0.5), transparent 50%),
    radial-gradient(at 10% 90%, rgba(10, 37, 64, 0.85), transparent 55%),
    linear-gradient(135deg, #0A2540 0%, var(--axon-blue-700) 55%, var(--axon-blue-600) 100%);
  color: var(--axon-white);
  border-radius: var(--axon-radius-xl);
  padding: clamp(48px, 6vw, 88px);
  display: flex;
  flex-direction: column;
  gap: 28px;
  align-items: flex-start;
  position: relative;
  overflow: hidden;
  /* Crisp icy rim + deep cool drop for an exquisite, glassy slab. */
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    var(--axon-shadow-lg);
}
.axon-contact-card::before {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(circle at 100% 0%, rgba(130, 197, 242, 0.22) 0%, transparent 40%);
  pointer-events: none;
}
/* Sharp royal-green tactical edge running down the left of the slab. */
.axon-contact-card::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 3px;
  background: linear-gradient(180deg, var(--axon-royal-500), var(--axon-royal-700));
  box-shadow: 0 0 18px rgba(0, 166, 81, 0.55);
  pointer-events: none;
  z-index: 3;
}
/* Green hexagonal molecular mesh — sits on the right, fades into the slab. */
.axon-contact-mesh {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 64%;
  pointer-events: none;
  z-index: 0;
  opacity: 0.6;
  -webkit-mask-image: linear-gradient(to left, #000 30%, transparent 100%);
  mask-image: linear-gradient(to left, #000 30%, transparent 100%);
}
/* Keep the copy above the decorative mesh + radial layers. */
.axon-contact-card > .axon-eyebrow-light,
.axon-contact-card > .axon-contact-title,
.axon-contact-card > .axon-button-on-green {
  position: relative;
  z-index: 2;
}
.axon-eyebrow-light {
  color: rgba(255, 255, 255, 0.85);
}
.axon-contact-title {
  font-size: clamp(1.8rem, 3.6vw, 2.8rem);
  margin: 0;
  max-width: 760px;
  color: var(--axon-white);
}
.axon-button-on-green {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  padding: 1rem 1.8rem;
  /* Pale-ice face with a royal-green inlay ring + inner glow. */
  background: linear-gradient(180deg, #FFFFFF 0%, #EFFBF4 100%);
  color: var(--axon-royal-700);
  border-radius: 999px;
  font-family: var(--axon-font-body);
  font-weight: 700;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  transition: transform 220ms ease, box-shadow 220ms ease, background 220ms ease;
  box-shadow:
    inset 0 0 0 1.5px rgba(0, 166, 81, 0.55),
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    inset 0 -8px 16px -10px rgba(0, 166, 81, 0.35),
    0 12px 28px -10px rgba(0, 60, 30, 0.4);
}
.axon-button-on-green:hover {
  transform: translateY(-2px);
  background: linear-gradient(180deg, #FFFFFF 0%, #E4F7EE 100%);
  box-shadow:
    inset 0 0 0 1.5px rgba(0, 166, 81, 0.8),
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    inset 0 -10px 18px -10px rgba(0, 166, 81, 0.5),
    0 18px 38px -12px rgba(0, 60, 30, 0.5);
}
.axon-button-on-green:hover .axon-arrow {
  transform: translateX(3px);
}

/* === Responsive === */
/* Makeen: below 1180 the absolute "cards on the curve" layout would crowd and
   overlap as the viewport narrows, so switch to a clean stacked grid well
   before that happens. */
@media (max-width: 1180px) {
  .axon-makeen {
    min-height: auto;
    margin-top: -120px;
    /* curve shrinks to 150px; clear it + give breathing room. */
    padding: 190px 24px 160px;
    display: block;
    /* Green must start right under the 150px curve — the desktop 380px line
       would leave a transparent (white) band here. */
    background:
      radial-gradient(120% 90% at 0% 100%, rgba(8, 13, 20, 0.55), transparent 60%),
      linear-gradient(
        to bottom,
        transparent 0,
        transparent 150px,
        #16202B 150px,
        #1B2733 55%,
        #223041 100%
      );
  }
  .axon-makeen-curve {
    height: 150px;
  }
  /* Points stack one-above-the-other below the curve (like mobile) — no
     horizontal crowding/overlap at any tablet width. Constrained width +
     centered so the cards don't stretch absurdly wide, with a big gap before
     the heading that follows. Height MUST be auto (the absolute 440px would
     clip the stacked cards). */
  .axon-makeen-points {
    position: static;
    height: auto;
    display: flex;
    flex-direction: column;
    gap: 22px;
    margin: 56px auto 72px;
    max-width: 560px;
  }
  .axon-makeen-point {
    position: static;
    left: auto;
    transform: none;
    width: 100%;
  }
  .axon-makeen-dot,
  .axon-makeen-line {
    display: none;
  }
  .axon-makeen-inner {
    gap: 48px;
  }
}

@media (max-width: 980px) {
  .axon-hero {
    /* full-viewport canvas on mobile too — text overlays absolute scene */
    min-height: 100vh;
    min-height: 100dvh;
    margin-top: -160px;
    padding: 220px 24px 60px;
  }
  /* DO NOT make .axon-hero-scene `position: relative` on mobile — keep it
     absolute so it doesn't take flow space and push everything down. */
  .axon-hero-fade {
    /* On mobile the text is layered on top of the canvas; fade the upper
       half of the screen to white-ish so the title/subtitle stay legible
       over busy neuron geometry. */
    background:
      linear-gradient(
        to bottom,
        rgba(255,255,255,0.78) 0%,
        rgba(255,255,255,0.45) 30%,
        rgba(255,255,255,0.0) 65%
      );
  }
  .axon-hero-grid {
    min-height: calc(100vh - 240px);
    padding-top: 0;
  }
  .axon-services-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .axon-offer-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .axon-cube-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }
  /* Hide the cube on mobile — interaction is fiddly on a phone, the cards
     alone carry the same information. Section becomes single-column with
     just the copy + cards. */
  .axon-cube-stage {
    display: none;
  }
  .axon-card-grid {
    grid-template-columns: 1fr 1fr;
  }

  .axon-hero-copy {
    padding: 0;
    margin: 0;
  }
  .axon-approach-grid {
    grid-template-columns: 1fr;
    gap: 32px;
  }
  /* Shallower angular cut on mobile — keep the slope + the twin lines tracking
     it at the smaller rise. */
  .axon-section-approach {
    margin-top: -52px;
    padding-top: 104px;
    clip-path: polygon(0 52px, 100% 0, 100% 100%, 0 100%);
  }
  .axon-section-approach::before {
    clip-path: polygon(0 52px, 100% 0, 100% 2.4px, 0 54.4px);
  }
  .axon-section-approach::after {
    clip-path: polygon(0 59px, 100% 7px, 100% 8.6px, 0 60.6px);
  }
  .axon-section {
    padding: 80px 24px;
  }
  .axon-section-cube {
    padding: 80px 24px;
  }
  /* Gentler diagonal + pull-up on mobile so the bright panel doesn't collide
     with the full-viewport hero copy. */
  .axon-section-offerings {
    margin-top: -70px;
    padding-top: 130px;
    clip-path: polygon(0 70px, 100% 0, 100% 100%, 0 100%);
  }
  .axon-section-offerings::before {
    clip-path: polygon(0 70px, 100% 0, 100% 1.5px, 0 71.5px);
  }
}

@media (max-width: 620px) {
  .axon-services-grid {
    grid-template-columns: 1fr;
  }
  .axon-offer-grid {
    grid-template-columns: 1fr;
  }
  .axon-makeen-points {
    margin: 44px auto 56px;
  }
  .axon-hero {
    padding: 24px 20px 60px;
  }
  .axon-section {
    padding: 64px 20px;
  }
  .axon-service-card {
    padding: 30px 26px 26px;
  }
}

/* === Contact message box (modal) === */
.axon-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(10, 37, 64, 0.55);
  backdrop-filter: blur(8px) saturate(120%);
  -webkit-backdrop-filter: blur(8px) saturate(120%);
}
.axon-modal {
  position: relative;
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  padding: clamp(28px, 4vw, 44px);
  border-radius: var(--axon-radius-xl);
  background: linear-gradient(180deg, #FFFFFF 0%, var(--axon-blue-050) 100%);
  border: 1px solid var(--axon-blue-100);
  /* Exquisite layered cool shadow + crisp top highlight + royal-green left edge. */
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    var(--axon-shadow-lg);
}
.axon-modal::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 3px;
  border-radius: var(--axon-radius-xl) 0 0 var(--axon-radius-xl);
  background: linear-gradient(180deg, var(--axon-royal-500), var(--axon-royal-700));
  box-shadow: 0 0 16px rgba(0, 166, 81, 0.5);
}
.axon-modal-close {
  position: absolute;
  top: 14px;
  right: 16px;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: var(--axon-ink-500);
  font-size: 1.6rem;
  line-height: 1;
  cursor: pointer;
  transition: background 200ms ease, color 200ms ease;
}
.axon-modal-close:hover {
  background: var(--axon-blue-100);
  color: var(--axon-ink-900);
}
.axon-modal-body {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.axon-modal-title {
  font-size: clamp(1.4rem, 2.6vw, 1.9rem);
  margin: 2px 0 0;
  color: var(--axon-ink-900);
}
.axon-modal-sub {
  font-size: 0.98rem;
  line-height: 1.5;
  color: var(--axon-ink-700);
  margin: 0 0 6px;
}
.axon-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.axon-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.axon-field-label {
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--axon-ink-700);
}
.axon-input {
  width: 100%;
  padding: 12px 14px;
  border-radius: var(--axon-radius-md);
  border: 1px solid var(--axon-blue-100);
  background: var(--axon-white);
  color: var(--axon-ink-900);
  font-family: var(--axon-font-body);
  font-size: 0.98rem;
  box-sizing: border-box;
  transition: border-color 200ms ease, box-shadow 200ms ease;
}
.axon-input::placeholder {
  color: var(--axon-ink-300);
}
.axon-input:focus {
  outline: none;
  border-color: var(--axon-blue-400);
  box-shadow: 0 0 0 4px rgba(75, 163, 232, 0.16);
}
.axon-textarea {
  resize: vertical;
  min-height: 110px;
  line-height: 1.5;
}
.axon-form-error {
  margin: 0;
  font-size: 0.88rem;
  color: #C0392B;
}
.axon-form-submit {
  align-self: flex-start;
  margin-top: 2px;
}
.axon-form-submit:disabled {
  opacity: 0.6;
  cursor: default;
  transform: none;
}
.axon-modal-success {
  align-items: flex-start;
  text-align: left;
}
.axon-success-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 999px;
  background: var(--axon-royal-500);
  color: #FFFFFF;
  font-size: 1.6rem;
  font-weight: 700;
  box-shadow: 0 0 0 6px rgba(0, 166, 81, 0.16);
  margin-bottom: 4px;
}
.axon-modal-fade-enter-active,
.axon-modal-fade-leave-active {
  transition: opacity 220ms ease;
}
.axon-modal-fade-enter-from,
.axon-modal-fade-leave-to {
  opacity: 0;
}
</style>
